// Start Call is blocked when prod services auto-stop sooner than this.
export const MIN_MINUTES_BEFORE_AUTO_STOP = 5;

// While prod services are starting, the status is re-checked this often...
export const STATUS_POLL_INTERVAL_MS = 15 * 1000;
// ...for at most this long after Start Prod Services, or after a flow is
// selected. Services usually report up within 5 minutes of a start.
export const STATUS_POLL_WINDOW_MS = 5 * 60 * 1000;

// Up only when server-automations has the flow scheduled AND the teleserver
// confirms its services.
export const isProdServicesUp = (prodServicesStatus) =>
  prodServicesStatus?.schedule_status === "Found" &&
  !!prodServicesStatus?.services_status;

// Whether to re-check the status. statusPoll is { until, afterStart }.
// Polling stops once the services are up or the window runs out. After a
// start it also continues while the status still reads Down, because the
// server can finish a start after the browser request has timed out.
export const shouldPollProdServicesStatus = (prodServicesStatus, statusPoll) => {
  if (Date.now() >= statusPoll.until || isProdServicesUp(prodServicesStatus)) {
    return false;
  }
  return (
    statusPoll.afterStart || prodServicesStatus?.schedule_status === "Found"
  );
};

const SECONDS_PER_UNIT = { day: 86400, hour: 3600, minute: 60, second: 1 };

// time_left_to_stop_prod is text built by the status API, e.g.
// "1 hour 4 minutes 3 seconds" or "Less than a second". Returns seconds, or
// null when there is no countdown ("NA" or anything unrecognised).
const parseTimeLeftToSeconds = (timeLeft) => {
  if (typeof timeLeft !== "string") return null;
  if (timeLeft === "Less than a second") return 0;

  const parts = [...timeLeft.matchAll(/(\d+) (day|hour|minute|second)s?/g)];
  if (parts.length === 0) return null;

  return parts.reduce(
    (total, [, value, unit]) => total + Number(value) * SECONDS_PER_UNIT[unit],
    0
  );
};

// Message to show instead of starting a call when prod services auto-stop
// within MIN_MINUTES_BEFORE_AUTO_STOP, or "" when the call can go ahead.
// The countdown is a snapshot from the last status fetch, so the time since
// then is subtracted. A status that is loading, missing or has no countdown
// never blocks the call.
export const getAutoStopWarning = (prodServicesStatus, statusLoading) => {
  if (statusLoading || prodServicesStatus?.schedule_status !== "Found") {
    return "";
  }

  const secondsAtFetch = parseTimeLeftToSeconds(
    prodServicesStatus.time_left_to_stop_prod
  );
  if (secondsAtFetch === null) return "";

  const secondsSinceFetch = prodServicesStatus.fetched_at
    ? (Date.now() - prodServicesStatus.fetched_at) / 1000
    : 0;
  const secondsLeft = secondsAtFetch - secondsSinceFetch;
  if (secondsLeft >= MIN_MINUTES_BEFORE_AUTO_STOP * 60) return "";

  const minutesLeft = Math.floor(secondsLeft / 60);
  const timeLeftText =
    minutesLeft >= 1 ? `${minutesLeft} min` : "less than 1 min";
  return `Services auto-stop in ${timeLeftText}, click Start Prod Services to continue testing.`;
};
